import React from 'react';

/**
 * Inline onboarding checklist that shows when user profile is incomplete.
 * Displays as collapsible cards that check off as items are completed.
 */
export default function OnboardingChecklist({
  profile,
  onOpenSettings,
  onUploadResume,
  allComplete = false
}) {
  // Check completion status for each item
  const hasName = profile?.name && profile.name.trim() !== '';
  const hasTargetRole = profile?.targetRole && profile.targetRole.trim() !== '';
  const hasSalary = (profile?.targetSalaryMin > 0 || profile?.targetSalaryMax > 0);
  const hasResume = profile?.experiences && profile.experiences.length > 0;

  const completedCount = [hasName, hasTargetRole, hasSalary, hasResume].filter(Boolean).length;
  const isAllComplete = completedCount === 4;

  // Debug logging
  console.log('🔍 OnboardingChecklist check:', {
    hasName,
    hasTargetRole,
    hasSalary,
    hasResume,
    completedCount,
    isAllComplete,
    salaryMin: profile?.targetSalaryMin,
    salaryMax: profile?.targetSalaryMax,
    allComplete,
  });

  // Don't render if all complete
  if (isAllComplete || allComplete) {
    console.log('❌ OnboardingChecklist: NOT rendering (all complete)');
    return null;
  }

  console.log('✅ OnboardingChecklist: RENDERING (incomplete)');

  const CheckIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" fill="#10b981" />
      <path d="M5 8l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const EmptyCircle = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="7" stroke="#d1d5db" strokeWidth="1.5" />
    </svg>
  );

  const ArrowRight = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  return (
    <div style={{
      background: 'linear-gradient(135deg, #fffef9 0%, #f5f0e6 100%)',
      borderRadius: '12px',
      border: '1px solid #e5e5e5',
      padding: '24px',
      marginBottom: '20px',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{
          margin: '0 0 8px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: '#2d2d2d',
        }}>
          Welcome to Manifold! Let's get you set up.
        </h3>
        <p style={{
          margin: 0,
          fontSize: '14px',
          color: '#6b7280',
        }}>
          Complete your profile to start analyzing jobs and generating tailored resumes.
        </p>
      </div>

      {/* Progress bar */}
      <div style={{
        marginBottom: '20px',
        background: '#e5e5e5',
        borderRadius: '4px',
        height: '6px',
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${(completedCount / 4) * 100}%`,
          height: '100%',
          background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
          borderRadius: '4px',
          transition: 'width 0.3s ease',
        }} />
      </div>

      {/* Checklist items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Name */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          background: hasName ? '#f0fdf4' : 'white',
          borderRadius: '8px',
          border: `1px solid ${hasName ? '#bbf7d0' : '#e5e5e5'}`,
        }}>
          {hasName ? <CheckIcon /> : <EmptyCircle />}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: hasName ? '#166534' : '#374151',
            }}>
              Your Name
            </div>
            {hasName && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {profile.name}
              </div>
            )}
          </div>
          {!hasName && (
            <button
              onClick={() => onOpenSettings?.('profile')}
              style={{
                background: '#f7c948',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#2d2d2d',
                cursor: 'pointer',
              }}
            >
              Add
            </button>
          )}
        </div>

        {/* Target Role */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          background: hasTargetRole ? '#f0fdf4' : 'white',
          borderRadius: '8px',
          border: `1px solid ${hasTargetRole ? '#bbf7d0' : '#e5e5e5'}`,
        }}>
          {hasTargetRole ? <CheckIcon /> : <EmptyCircle />}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: hasTargetRole ? '#166534' : '#374151',
            }}>
              Target Role
            </div>
            {hasTargetRole && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {profile.targetRole}
              </div>
            )}
          </div>
          {!hasTargetRole && (
            <button
              onClick={() => onOpenSettings?.('profile')}
              style={{
                background: '#f7c948',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#2d2d2d',
                cursor: 'pointer',
              }}
            >
              Add
            </button>
          )}
        </div>

        {/* Target Salary */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          background: hasSalary ? '#f0fdf4' : 'white',
          borderRadius: '8px',
          border: `1px solid ${hasSalary ? '#bbf7d0' : '#e5e5e5'}`,
        }}>
          {hasSalary ? <CheckIcon /> : <EmptyCircle />}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: hasSalary ? '#166534' : '#374151',
            }}>
              Target Salary Range
            </div>
            {hasSalary && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                ${(profile.targetSalaryMin / 1000).toFixed(0)}K - ${(profile.targetSalaryMax / 1000).toFixed(0)}K
              </div>
            )}
          </div>
          {!hasSalary && (
            <button
              onClick={() => onOpenSettings?.('profile')}
              style={{
                background: '#f7c948',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#2d2d2d',
                cursor: 'pointer',
              }}
            >
              Add
            </button>
          )}
        </div>

        {/* Resume */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 16px',
          background: hasResume ? '#f0fdf4' : 'white',
          borderRadius: '8px',
          border: `1px solid ${hasResume ? '#bbf7d0' : '#e5e5e5'}`,
        }}>
          {hasResume ? <CheckIcon /> : <EmptyCircle />}
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: hasResume ? '#166534' : '#374151',
            }}>
              Resume / Work History
            </div>
            {hasResume && (
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                {profile.experiences.length} experience{profile.experiences.length === 1 ? '' : 's'} added
              </div>
            )}
          </div>
          {!hasResume && (
            <button
              onClick={() => onOpenSettings?.('resume')}
              style={{
                background: '#f7c948',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#2d2d2d',
                cursor: 'pointer',
              }}
            >
              Upload
            </button>
          )}
        </div>
      </div>

      {/* Next step hint - only show when all complete */}
      {isAllComplete && (
        <div style={{
          marginTop: '20px',
          padding: '16px',
          background: '#f0fdf4',
          borderRadius: '8px',
          border: '1px solid #bbf7d0',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#10b981',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
          }}>
            <CheckIcon />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#166534' }}>
              You're all set!
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
              Now paste a job URL above to see your match and get a tailored resume.
            </div>
          </div>
          <div style={{ color: '#10b981' }}>
            <ArrowRight />
          </div>
        </div>
      )}

      {/* Completion status */}
      <div style={{
        marginTop: '16px',
        fontSize: '12px',
        color: '#9ca3af',
        textAlign: 'center',
      }}>
        {completedCount} of 4 complete
      </div>
    </div>
  );
}
